// Pure core of the Results "English" display (bug939): no React, axios or timers, so the passes
// can be exercised in node with injected process/read/wait fakes.
//
// English is display-only over the original rows: every row shows at once, with cached English
// where it exists and the original otherwise. Picking English (and every scroll pause) asks for
// the rows on screen plus one screen of lookahead; one scheduler works on the latest such window.
// Leaving English, or the page, stops scheduling; a request already sent may still finish.

export const TRANSLATE_MODE_ORIGINAL = "ORIGINAL";
export const TRANSLATE_MODE_ENGLISH = "ENGLISH";

export const TRANSLATE_STATUS_IDLE = "idle";
export const TRANSLATE_STATUS_RUNNING = "running";
export const TRANSLATE_STATUS_DONE = "done";
export const TRANSLATE_STATUS_ERROR = "error";

export const TRANSLATE_ERROR_STALE = "STALE";
export const TRANSLATE_ERROR_ANONYMITY = "ANONYMITY";
export const TRANSLATE_ERROR_DISABLED = "DISABLED";
export const TRANSLATE_ERROR_UNAVAILABLE = "UNAVAILABLE";
export const TRANSLATE_ERROR_GENERIC = "GENERIC";

export const TRANSLATE_NOTICE_BUSY = "BUSY";
export const TRANSLATE_NOTICE_RATE_LIMITED = "RATE_LIMITED";

export const TRANSLATE_OUTCOME_DONE = "done";
export const TRANSLATE_OUTCOME_ERROR = "error";
export const TRANSLATE_OUTCOME_REVERT = "revert";
export const TRANSLATE_OUTCOME_CANCELLED = "cancelled";

export const TRANSLATE_PASS_WINDOW = "window";

// A process call may use the BE's 25 s budget plus a provider retry, so allow more than that.
export const TRANSLATE_REQUEST_TIMEOUT_MS = 45000;
export const TRANSLATE_MAX_RESULT_IDS = 200;
export const TRANSLATE_WINDOW_DEBOUNCE_MS = 400;
export const TRANSLATE_MAX_STRIKES = 3;
export const TRANSLATE_MAX_WAITS = 30;
export const TRANSLATE_MAX_WINDOW_BATCHES = 8;
export const TRANSLATE_ERROR_WAIT_MS = 2000;
export const TRANSLATE_BUSY_WAIT_MS = 2000;
export const TRANSLATE_RATE_LIMIT_WAIT_MS = 5000;
const TRANSLATE_MAX_SERVER_WAIT_MS = 60000;
// Rows per screen when the table cannot be measured (not rendered yet, or off screen).
const TRANSLATE_FALLBACK_ROW_HEIGHT = 70;

// The BE keys a Message answer with this reserved question id; the FE keys it by result id alone.
export const MESSAGE_QUESTION_ID = "__message__";
const DISPLAY_PENDING = "PENDING";
const STALE_BODY_PATTERN = /Cannot (POST|GET)/i;
const UNAVAILABLE_CODES = ["NOT_AUTHORIZED", "TRIVVY_NOT_FOUND", "UNAUTHENTICATED", "MISSING_ORGANIZATION"];

// Messages keys are "<resultId>"; Survey/KC keys are "<resultId>:<questionId>".
export const buildTextKey = (resultId, questionId) => {
  if (questionId && questionId !== MESSAGE_QUESTION_ID) {
    return `${resultId}:${questionId}`;
  }
  return `${resultId}`;
};

// Order-independent signature of a set of result ids (a re-sort is not a new set of rows).
export const signatureOf = (ids) => ids.slice().sort().join(",");

export const chunkIds = (ids, size) => {
  const chunks = [];
  for (let at = 0; at < ids.length; at += size) {
    chunks.push(ids.slice(at, at + size));
  }
  return chunks;
};

// ---------- English cache (display text only; originals are never stored here) ----------

export const createEnglishCache = () => ({
  answers: {},
  questions: {},
  name: null,
  messagingSubject: null,
  sharedComplete: false,
  sharedRead: false,
  masked: false,
  readIds: {},
  pendingIds: {},
});

const textOf = (item) =>
  item && typeof item.displayText === "string" && item.displayText.length > 0 ? item.displayText : null;

const setValue = (target, key, value) => {
  if (value !== null) {
    if (target[key] !== value) {
      target[key] = value;
      return 1;
    }
    return 0;
  }
  if (Object.prototype.hasOwnProperty.call(target, key)) {
    delete target[key];
    return 1;
  }
  return 0;
};

// Shared definitions: question titles, options by option id, scale labels, a Message's copy.
export const mergeShared = (cache, shared) => {
  if (!cache || !shared || typeof shared !== "object") {
    return 0;
  }
  let changed = 0;
  cache.sharedRead = true;
  cache.sharedComplete = shared.complete === true;
  const name = textOf(shared.name);
  const subject = textOf(shared.messagingSubject);
  if (cache.name !== name) {
    cache.name = name;
    changed += 1;
  }
  if (cache.messagingSubject !== subject) {
    cache.messagingSubject = subject;
    changed += 1;
  }
  (Array.isArray(shared.questions) ? shared.questions : []).forEach((question) => {
    if (!question || !question.questionId) {
      return;
    }
    const next = { text: textOf(question), options: {}, minLabel: textOf(question.minLabel), maxLabel: textOf(question.maxLabel) };
    (Array.isArray(question.options) ? question.options : []).forEach((option) => {
      const english = textOf(option);
      if (option && option.optionId && english !== null) {
        next.options[String(option.optionId)] = english;
      }
    });
    const key = String(question.questionId);
    if (JSON.stringify(cache.questions[key]) !== JSON.stringify(next)) {
      cache.questions[key] = next;
      changed += 1;
    }
  });
  return changed;
};

// Per-result answers [{ questionId, displayText, displayStatus }]. Only TRANSLATED answers carry
// English; any other status removes a stale entry so the original shows.
export const mergeAnswers = (cache, resultId, answers) => {
  if (!cache || resultId === undefined || resultId === null) {
    return 0;
  }
  let changed = 0;
  const id = String(resultId);
  let pending = false;
  (Array.isArray(answers) ? answers : []).forEach((answer) => {
    if (!answer || !answer.questionId) {
      return;
    }
    if (answer.displayStatus === DISPLAY_PENDING) {
      pending = true;
    }
    changed += setValue(cache.answers, buildTextKey(id, answer.questionId), textOf(answer));
  });
  cache.readIds[id] = true;
  if (pending) {
    cache.pendingIds[id] = true;
  } else {
    delete cache.pendingIds[id];
  }
  return changed;
};

// GET translations: { shared, results: [{ resultId, answers }], employeeTextMasked }.
export const mergeRead = (cache, data) => {
  if (!cache || !data || typeof data !== "object") {
    return 0;
  }
  let changed = mergeShared(cache, data.shared);
  cache.masked = data.employeeTextMasked === true;
  (Array.isArray(data.results) ? data.results : []).forEach((result) => {
    if (result && result.resultId) {
      changed += mergeAnswers(cache, result.resultId, result.answers);
    }
  });
  return changed;
};

// Rows from a By User endpoint called with displayLanguage=en: the English sits beside the
// untouched originals (responses[].displayText, messagingResponse.displayText).
export const mergeRows = (cache, rows, meta) => {
  if (!cache || !Array.isArray(rows)) {
    return 0;
  }
  let changed = 0;
  if (meta && meta.shared) {
    changed += mergeShared(cache, meta.shared);
    cache.masked = meta.employeeTextMasked === true;
  }
  rows.forEach((row) => {
    if (!row || !row._id) {
      return;
    }
    const answers = [];
    if (row.messagingResponse && row.messagingResponse.displayStatus) {
      answers.push({ questionId: MESSAGE_QUESTION_ID, displayText: row.messagingResponse.displayText, displayStatus: row.messagingResponse.displayStatus });
    }
    (Array.isArray(row.responses) ? row.responses : []).forEach((response) => {
      if (response && response.question_id && response.displayStatus) {
        answers.push({ questionId: String(response.question_id), displayText: response.displayText, displayStatus: response.displayStatus });
      }
    });
    if (answers.length > 0) {
      changed += mergeAnswers(cache, row._id, answers);
    }
  });
  return changed;
};

export const pickText = (cache, resultId, questionId, original) => {
  if (!cache) {
    return original;
  }
  const text = cache.answers[buildTextKey(resultId, questionId)];
  return typeof text === "string" && text.length > 0 ? text : original;
};

export const pickQuestionText = (cache, questionId, original) => {
  const question = cache ? cache.questions[String(questionId)] : null;
  return question && question.text ? question.text : original;
};

export const pickOptionText = (cache, questionId, optionId, original) => {
  const question = cache ? cache.questions[String(questionId)] : null;
  if (!question || optionId === undefined || optionId === null) {
    return original;
  }
  const english = question.options[String(optionId)];
  return english ? english : original;
};

// end: "min" or "max".
export const pickScaleLabel = (cache, questionId, end, original) => {
  const question = cache ? cache.questions[String(questionId)] : null;
  if (!question) {
    return original;
  }
  const english = end === "min" ? question.minLabel : question.maxLabel;
  return english ? english : original;
};

// field: "name" or "messagingSubject".
export const pickMessageText = (cache, field, original) => {
  const english = cache ? cache[field] : null;
  return english ? english : original;
};

// Ids not read yet, or with an answer still pending.
export const unsettledIds = (cache, ids) =>
  ids.filter((id) => !cache.readIds[String(id)] || cache.pendingIds[String(id)]);

// ---------- visible rows ----------

// rows: the row elements of a table in data order (index i = ids[i]); clip: the scroll container
// (or null when the page itself scrolls). Returns the ids on screen plus one screen of lookahead,
// at most TRANSLATE_MAX_RESULT_IDS. Rows are measured, so variable heights and page scrolling
// on phones are handled alike.
export const pickWindowIds = (rows, clip, ids, viewportHeight) => {
  const list = Array.isArray(ids) ? ids : [];
  const elements = Array.isArray(rows) ? rows : [];
  const firstHeight = elements.length > 0 && elements[0].getBoundingClientRect
    ? elements[0].getBoundingClientRect().height
    : 0;
  const rowHeight = firstHeight > 0 ? firstHeight : TRANSLATE_FALLBACK_ROW_HEIGHT;
  const fallback = () =>
    list.slice(0, Math.min(TRANSLATE_MAX_RESULT_IDS, Math.ceil((2 * viewportHeight) / rowHeight)));
  if (elements.length === 0 || !viewportHeight) {
    return fallback();
  }
  let top = 0;
  let bottom = viewportHeight;
  if (clip && clip.getBoundingClientRect) {
    const rect = clip.getBoundingClientRect();
    top = Math.max(rect.top, 0);
    bottom = Math.min(rect.bottom, viewportHeight);
  }
  if (bottom <= top) {
    return fallback();
  }
  const limit = bottom + (bottom - top);
  // Rows are laid out top to bottom: binary search the first row ending below the top edge.
  let low = 0;
  let high = elements.length - 1;
  let first = elements.length;
  while (low <= high) {
    const mid = Math.floor((low + high) / 2);
    if (elements[mid].getBoundingClientRect().bottom > top) {
      first = mid;
      high = mid - 1;
    } else {
      low = mid + 1;
    }
  }
  const picked = [];
  for (let at = first; at < elements.length && at < list.length && picked.length < TRANSLATE_MAX_RESULT_IDS; at += 1) {
    if (elements[at].getBoundingClientRect().top >= limit) {
      break;
    }
    picked.push(list[at]);
  }
  return picked;
};

// ---------- errors ----------

const errorsOf = (err) => {
  const data = err && err.response && err.response.data;
  if (data && typeof data === "object" && data.errors && typeof data.errors === "object") {
    return data.errors;
  }
  return null;
};

// Retry-After header first, then the body hints, capped so a bad value never parks the loop.
const serverWaitMs = (err, fallbackMs) => {
  const headers = err && err.response && err.response.headers;
  const errors = errorsOf(err);
  let ms = 0;
  const header = headers ? Number(headers["retry-after"]) : 0;
  if (header > 0) {
    ms = header * 1000;
  } else if (errors && Number(errors.retryAfterSeconds) > 0) {
    ms = Number(errors.retryAfterSeconds) * 1000;
  } else if (errors && errors.progress && Number(errors.progress.retryAfterMs) > 0) {
    ms = Number(errors.progress.retryAfterMs);
  }
  if (ms > 0) {
    return Math.min(ms, TRANSLATE_MAX_SERVER_WAIT_MS);
  }
  return fallbackMs;
};

// HTML "Cannot POST/GET" = stale BE deploy; otherwise the JSON errors.code decides what happens.
// `stop`: no more processing, but cached English already read stays on screen.
export const classifyTranslateError = (err) => {
  const response = err && err.response;
  if (!response) {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: true, waitMs: TRANSLATE_ERROR_WAIT_MS };
  }
  if (typeof response.data === "string" && STALE_BODY_PATTERN.test(response.data)) {
    return { kind: TRANSLATE_ERROR_STALE, retryable: false, revert: true };
  }
  const errors = errorsOf(err);
  const code = errors ? errors.code || errors.status : null;
  if (code === "TRANSLATION_DISABLED") {
    return { kind: TRANSLATE_ERROR_DISABLED, retryable: false, stop: true };
  }
  if (UNAVAILABLE_CODES.indexOf(code) !== -1) {
    return { kind: TRANSLATE_ERROR_UNAVAILABLE, retryable: false, revert: true };
  }
  if (response.status === 429) {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: false, notice: TRANSLATE_NOTICE_RATE_LIMITED, waitMs: serverWaitMs(err, TRANSLATE_RATE_LIMIT_WAIT_MS) };
  }
  if (code === "BUSY") {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: false, notice: TRANSLATE_NOTICE_BUSY, waitMs: serverWaitMs(err, TRANSLATE_BUSY_WAIT_MS) };
  }
  if (code === "MODEL_UNAVAILABLE") {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: true, strike: true, notice: TRANSLATE_NOTICE_RATE_LIMITED, waitMs: serverWaitMs(err, TRANSLATE_RATE_LIMIT_WAIT_MS) };
  }
  // The provider rejected the request itself (bad key, bad input): retrying cannot help.
  if (code === "MODEL_ERROR") {
    return { kind: TRANSLATE_ERROR_GENERIC, retryable: false };
  }
  const retryable = response.status >= 500 || response.status === 408;
  return { kind: TRANSLATE_ERROR_GENERIC, retryable: retryable, strike: true, waitMs: TRANSLATE_ERROR_WAIT_MS };
};

// ---------- the scheduler ----------

export const createRunState = () => ({
  strikes: 0,
  waits: 0,
  noticeShown: false,
});

const toCount = (value) => (typeof value === "number" && isFinite(value) && value > 0 ? value : 0);

const CANCELLED = { outcome: TRANSLATE_OUTCOME_CANCELLED };
const errorOutcome = () => ({ outcome: TRANSLATE_OUTCOME_ERROR, kind: TRANSLATE_ERROR_GENERIC });

// One request with bounded waits and strikes: { value } or { stop }.
const makeAttempt = (deps, state) => async (request) => {
  const { wait, isCancelled, onUpdate } = deps;
  while (true) {
    if (isCancelled()) {
      return { stop: CANCELLED };
    }
    try {
      const value = await request();
      if (isCancelled()) {
        return { stop: CANCELLED };
      }
      if (!value || typeof value !== "object") {
        throw new Error("Invalid translation response");
      }
      state.strikes = 0;
      state.waits = 0;
      if (state.noticeShown) {
        state.noticeShown = false;
        onUpdate({ notice: null });
      }
      return { value: value };
    } catch (err) {
      if (isCancelled()) {
        return { stop: CANCELLED };
      }
      const info = classifyTranslateError(err);
      if (info.revert) {
        return { stop: { outcome: TRANSLATE_OUTCOME_REVERT, kind: info.kind } };
      }
      if (info.stop) {
        return { stop: { outcome: TRANSLATE_OUTCOME_ERROR, kind: info.kind } };
      }
      if (!info.retryable) {
        return { stop: errorOutcome() };
      }
      if (info.strike) {
        state.strikes += 1;
        if (state.strikes >= TRANSLATE_MAX_STRIKES) {
          return { stop: errorOutcome() };
        }
      }
      state.waits += 1;
      if (state.waits > TRANSLATE_MAX_WAITS) {
        return { stop: errorOutcome() };
      }
      state.noticeShown = Boolean(info.notice);
      onUpdate({ notice: info.notice || null });
      await wait(info.waitMs || TRANSLATE_ERROR_WAIT_MS);
    }
  }
};

// Reads cached English for ids, in chunks of 200, merging each answer by result id + question id.
const readInto = async (deps, attempt, ids) => {
  const chunks = chunkIds(ids, TRANSLATE_MAX_RESULT_IDS);
  if (chunks.length === 0) {
    chunks.push([]);
  }
  for (let at = 0; at < chunks.length; at += 1) {
    const result = await attempt(() => deps.read(chunks[at]));
    if (result.stop) {
      return result.stop;
    }
    if (result.value) {
      deps.onUpdate({ read: result.value });
    }
  }
  return null;
};

// True when every id has a final answer in the cache and the shared copy is complete: the
// page already holds everything for these rows, so nothing is requested again.
export const isWindowSettled = (cache, ids) =>
  Boolean(cache) && cache.sharedComplete && unsettledIds(cache, ids).length === 0;

// The one translation scheduler. Each step works on the CURRENT window (deps.getWindowIds()
// re-read every time), so work for rows the user scrolled away from is never sent; only a request
// already dispatched finishes, and its answers still land (by result id + question id, never row
// position). For each new window: cached English first, then bounded processing requests for its
// rows still without a final answer, each followed by a read of those rows. Settled rows are never
// requested again. Resolves { outcome, windowSignature } with the window it last worked on.
// deps: { process(body), read(ids), cache, getWindowIds(), wait, isCancelled, onUpdate }
export const runWindowPass = async (deps, state) => {
  const attempt = makeAttempt(deps, state);
  let windowSignature = null;
  let readFor = null;
  let batches = 0;
  while (true) {
    if (deps.isCancelled()) {
      return CANCELLED;
    }
    const ids = deps.getWindowIds();
    const signature = signatureOf(ids);
    if (signature !== windowSignature) {
      windowSignature = signature;
      batches = 0;
    }
    if (isWindowSettled(deps.cache, ids)) {
      return { outcome: TRANSLATE_OUTCOME_DONE, windowSignature: windowSignature };
    }
    // Cached English first (no processing), once per window.
    if (readFor !== signature) {
      readFor = signature;
      const stop = await readInto(deps, attempt, unsettledIds(deps.cache, ids));
      if (stop) {
        return stop;
      }
      continue;
    }
    // A window that stays unsettled (another admin holds its rows, or a retry is not due yet) is
    // given up after a few requests; the next window change schedules it again.
    if (batches >= TRANSLATE_MAX_WINDOW_BATCHES) {
      return { outcome: TRANSLATE_OUTCOME_DONE, windowSignature: windowSignature };
    }
    batches += 1;
    const missing = unsettledIds(deps.cache, ids).slice(0, TRANSLATE_MAX_RESULT_IDS);
    const result = await attempt(() => deps.process({ mode: TRANSLATE_PASS_WINDOW, resultIds: missing }));
    if (result.stop) {
      return result.stop;
    }
    const progress = result.value || {};
    // The dispatched batch's answers, even if the window moved meanwhile.
    const stop = await readInto(deps, attempt, missing);
    if (stop) {
      return stop;
    }
    if (toCount(progress.processed) === 0 && !isWindowSettled(deps.cache, deps.getWindowIds())) {
      await deps.wait(Math.min(TRANSLATE_MAX_SERVER_WAIT_MS, Math.max(1000, toCount(progress.retryAfterMs) || TRANSLATE_BUSY_WAIT_MS)));
    }
  }
};
